import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { createPayment, generateInvoiceId, loadEPayWidget } from "../../services/ePayService";
import styled from "styled-components";
import tw from "twin.macro";
import errorMonitoring from "../../utils/errorMonitoring";
import { getPaymentErrorMessage } from "../../utils/errorMessages";

const PaymentButton = styled.button`
    ${tw`px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300`}
    background: ${props => props.disabled ? '#94A3B8' : '#0ABD19'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

    &:hover {
        background: ${props => props.disabled ? '#94A3B8' : '#089714'};
        transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
        box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(10, 189, 25, 0.3)'};
    }

    &:active {
        transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
    }
`;

const LoadingSpinner = styled.div`
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.6s linear infinite;
    margin-right: 8px;

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
`;

/**
 * Компонент для оплаты через Halyk Bank ePay
 *
 * Props:
 * - amount: сумма платежа (обязательно)
 * - description: описание платежа
 * - onSuccess: callback при успешной оплате
 * - onFailure: callback при ошибке
 * - buttonText: текст кнопки (по умолчанию "Оплатить")
 */
export default function HalykPaymentWidget({
    amount,
    description = "Оплата услуг Eparcel",
    onSuccess,
    onFailure,
    buttonText = "Оплатить через Halyk ePay",
    disabled = false
}) {
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [widgetReady, setWidgetReady] = useState(false);

    // Загружаем виджет при монтировании
    useEffect(() => {
        loadEPayWidget()
            .then(() => {
                setWidgetReady(true);
            })
            .catch((error) => {
                console.error('Ошибка загрузки ePay виджета:', error);
                errorMonitoring.captureException(error, {
                    action: 'load_epay_widget'
                });
                toast.error('Не удалось загрузить платежную систему');
            });
    }, []);

    // Валидация суммы
    useEffect(() => {
        if (amount && (isNaN(amount) || amount <= 0)) {
            console.error('Некорректная сумма платежа:', amount);
        }
    }, [amount]);

    const handlePayment = async () => {
        // Валидация
        if (!amount || amount <= 0) {
            toast.error('Некорректная сумма платежа');
            return;
        }

        if (!user) {
            toast.error('Необходимо войти в систему');
            return;
        }

        if (!widgetReady) {
            toast.error('Платежная система еще загружается. Подождите...');
            return;
        }

        setIsLoading(true);

        try {
            // Генерируем уникальный ID платежа
            const invoiceId = generateInvoiceId();

            // Формируем данные платежа
            const paymentAmount = parseFloat(amount);
            const paymentData = {
                amount: paymentAmount,
                description,
                accountId: user.uid,
                customerName: user.displayName || user.email?.split('@')[0] || 'Клиент',
                customerEmail: user.email,
                invoiceId,
                successUrl: `https://www.eparcel.kz/PaymentSuccess?invoice=${invoiceId}&amount=${paymentAmount}&userId=${user.uid}`,
                failureUrl: `https://www.eparcel.kz/PaymentSuccess?status=failed&invoice=${invoiceId}`
            };

            console.log('💳 Creating payment with data:', {
                invoiceId,
                amount: paymentAmount,
                userId: user.uid,
                successUrl: paymentData.successUrl
            });

            // Создаем платеж (произойдет переадресация)
            await createPayment(paymentData);

            // Track редиректа на оплату
            errorMonitoring.addBreadcrumb('Payment redirect initiated', {
                invoiceId,
                amount,
                userId: user.uid
            });

            // Этот код может не выполниться из-за редиректа
            toast.success('Переадресация на страницу оплаты...');

            // Примечание: после редиректа пользователь вернется на PaymentSuccess страницу

        } catch (error) {
            console.error('Ошибка при оплате:', error);

            // Логируем ошибку платежа
            errorMonitoring.captureException(error, {
                action: 'payment_failed',
                amount,
                userId: user?.uid,
                description
            });

            // Показываем понятное сообщение
            const errorMessage = getPaymentErrorMessage(error?.code) ||
                                error?.message ||
                                'Не удалось выполнить платеж. Попробуйте позже.';
            toast.error(errorMessage);

            if (onFailure) {
                onFailure(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const isDisabled = disabled || isLoading || !widgetReady || !amount || amount <= 0;

    return (
        <PaymentButton
            onClick={handlePayment}
            disabled={isDisabled}
            type="button"
        >
            {isLoading && <LoadingSpinner />}
            {isLoading ? 'Обработка...' : buttonText}
        </PaymentButton>
    );
}
